import React, { useMemo, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Button,
  Flex,
  Icon,
  Text,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaTools, FaFlask } from "react-icons/fa";
import { MdSupportAgent } from "react-icons/md";
import CollapsibleLeftNav from "../../components/common/CollapsibleLeftNav";
import AgentTable from "../../components/new_darwin_eva/AgentTable";
import ToolTable from "../../components/new_darwin_eva/ToolTable";
import ExperimentTable from "../../components/new_darwin_eva/ExperimentTable";
import EvaPlayground from "components/new_darwin_eva/EvaPlayground";
import ChatPage from "pages/chat/ChatPage";
import { useAnalystChat } from "context/AnalystChatContext";
import { ChatClientType } from "build-ts/chat_session";
import { DarwinAgentType } from "build-ts/common";
import { AddIcon } from "@chakra-ui/icons";
import { FiCodesandbox } from "react-icons/fi";
import { GoHubot } from "react-icons/go";

const EvaListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get EVA sessions from context
  const {
    evaChatSessionsList,
    selectedEvaSessionId,
    setSelectedEvaSessionId,
    fetchEvaChatSessionsList,
    isLoadingEvaSessions,
    deleteChatSession,
  } = useAnalystChat();

  const routeState = location.state as undefined | {
    initialDarwinAgentId?: string;
    initialDarwinAgentType?: DarwinAgentType;
    initialPromptId?: string;
    startAtStepIndex?: number;
  };

  // Determine active section from URL pathname
  const activeSection = useMemo(() => {
    const pathname = location.pathname;
    if (pathname.startsWith("/eva/chat")) {
      return "chatSessions";
    } else if (pathname.startsWith("/eva/playground")) {
      return "playground";
    } else if (pathname.startsWith("/eva/tools")) {
      return "tools";
    } else if (pathname.startsWith("/eva/experiments")) {
      return "experiments";
    } else if (pathname.startsWith("/eva/agents")) {
      return "agents";
    }
    return "agents"; // default
  }, [location.pathname]);

  // Fetch EVA sessions on mount
  useEffect(() => {
    fetchEvaChatSessionsList("");
  }, []);

  // Extract chat ID from URL and set as selected
  // Clear selection when navigating away from chat pages
  useEffect(() => {
    const pathname = location.pathname;
    if (pathname.startsWith("/eva/chat/")) {
      const chatId = pathname.split("/eva/chat/")[1];
      if (chatId && chatId !== selectedEvaSessionId) {
        setSelectedEvaSessionId(chatId);
      }
    } else if (selectedEvaSessionId) {
      // Clear selection when navigating away from chat pages
      setSelectedEvaSessionId("");
    }
  }, [location.pathname, selectedEvaSessionId, setSelectedEvaSessionId]);

  const handleCreate = () => {
    navigate("/eva/agent/create");
  };

  const handleAgentsClick = () => {
    navigate("/eva/agents");
  };

  const handleToolsClick = () => {
    navigate("/eva/tools");
  };

  const handlePlaygroundClick = () => {
    navigate("/eva/playground");
  };

  const handleExperimentsClick = () => {
    navigate("/eva/experiments");
  };

  // Handle EVA session selection
  const handleSessionSelect = (sessionId: string) => {
    navigate(`/eva/chat/${sessionId}`);
  };

  // Handle EVA session deletion
  const handleSessionDelete = (sessionId: string) => {
    deleteChatSession(sessionId, ChatClientType.EVA);
  };

  // Navigation items configuration (removed Saved Chats - now shown in sidebar)
  const navItems = [
    {
      id: "agents",
      label: "Agents",
      icon: GoHubot,
      iconBoxSize: 6,
      onClick: handleAgentsClick,
    },
    {
      id: "tools",
      label: "Tools",
      icon: FaTools,
      iconBoxSize: 5,
      onClick: handleToolsClick,
    },
    {
      id: "playground",
      label: "Playground",
      icon: FiCodesandbox,
      iconBoxSize: 6,
      onClick: handlePlaygroundClick,
    },
    {
      id: "experiments",
      label: "Experiments",
      icon: FaFlask,
      iconBoxSize: 5,
      onClick: handleExperimentsClick,
    },

  ];

  return (
    <Flex height="100%" gap={.5}>
      {/* Left Navigation Sidebar */}
      <CollapsibleLeftNav
        title="Eva"
        titleIcon={MdSupportAgent}
        items={navItems}
        activeItemId={activeSection}
        defaultWidth={200}
        minWidth={180}
        maxWidth={350}
        // EVA session list props
        isFromEvaPlayground={true}
        sessions={evaChatSessionsList}
        selectedSessionId={selectedEvaSessionId}
        onSessionSelect={handleSessionSelect}
        onSessionDelete={handleSessionDelete}
        onSessionsRefresh={() => fetchEvaChatSessionsList("")}
        isLoadingSessions={isLoadingEvaSessions}
      />

      {/* Main Content Area */}
      <Box bg="bg.surface" boxShadow="lg" borderRadius="lg" overflowY="hidden" flex="1">
      {activeSection === "agents" && (
          <VStack
            className="eva-list-content"
            spacing={3}
            p={5}
            height="100%"
            alignItems="stretch"
          >
            <HStack justify="space-between" w="full" px={3} flexShrink={0}>
              <HStack spacing={4}>
                <Icon as={GoHubot} boxSize={8} color="fg.default" />
                <Text fontSize="lg" mb={0} fontWeight="700">Manage Agents</Text>
              </HStack>
              <Button
                onClick={handleCreate}
                colorScheme="purple"
                size="md"
                bg="purple.700"
                color="white"
                _hover={{ bg: "purple.600" }}
                leftIcon={<Icon as={AddIcon} boxSize={3} color="white" />}
              >
                New Agent
              </Button>
            </HStack>
            <Box flex="1" overflow="hidden" width="100%">
              <AgentTable />
            </Box>
          </VStack>
        )}
        {activeSection === "tools" && (
          <VStack
           className="eva-list-content"
           spacing={3}
           p={5}
           height="100%"
           alignItems="stretch"
         >
           <HStack justify="space-between" w="full" px={3} flexShrink={0}>
             <HStack spacing={4}>
               <Icon as={FaTools} boxSize={7} color="fg.default" />
               <Text fontSize="lg" mb={0} fontWeight="700">Manage Tools</Text>
             </HStack>
           </HStack>
           <Box flex="1" overflow="hidden" width="100%">
             <ToolTable />
           </Box>
         </VStack>
        )}
        {activeSection === "experiments" && (
          <VStack className="eva-list-content" spacing={3} p={5} height="100%" alignItems="stretch">
            <HStack justify="space-between" w="full" px={3} flexShrink={0}>
              <HStack spacing={4}>
                <Icon as={FaFlask} boxSize={7} color="fg.default" />
                <Text fontSize="lg" mb={0} fontWeight="700">Manage Experiments</Text>
              </HStack>
            </HStack>
            <Box flex="1" overflow="hidden" width="100%">
              <ExperimentTable />
            </Box>
          </VStack>
        )}
        {activeSection === "playground" && (

          <EvaPlayground
            initialDarwinAgentId={routeState?.initialDarwinAgentId}
            initialDarwinAgentType={routeState?.initialDarwinAgentType}
            initialPromptId={routeState?.initialPromptId}
            startAtStepIndex={routeState?.startAtStepIndex}
          />
        )}
        {activeSection === "chatSessions" && (
          <ChatPage
            isFromEva={true}
            initialChatSessionId={selectedEvaSessionId || undefined}
          />
        )}
      </Box>
    </Flex>
  );
};

export default EvaListPage;
